import React, { useEffect, useState, useMemo, useRef } from "react";
import { api } from "../lib/api";
import { usePersonalization } from "../lib/personalization";
import { useContent } from "../lib/content";
import { useBranding } from "../components/Layout";
import { Check, Sparkles, Plus, X, MessageCircle, Printer, Save, EyeOff, RotateCcw } from "lucide-react";
import { PREP_CATEGORIES } from "../lib/data";

const IMAGES = {
  repairs: "/prep-guide/repairs.jpg",
  purges: "/prep-guide/purges.jpg",
  photo_prep: "/prep-guide/photo_prep.jpg",
  exterior: "/prep-guide/exterior.jpg",
  kitchen: "/prep-guide/kitchen.jpg",
  living_room: "/prep-guide/living_room.jpg",
  bedrooms: "/prep-guide/bedrooms.jpg",
  bathrooms: "/prep-guide/bathrooms.jpg",
  miscellaneous: "/prep-guide/miscellaneous.jpg",
};
const BLURBS = {
  repairs: "Small fixes that keep buyers focused on the good stuff.",
  purges: "Pack it, donate it, or hide it — buyers imagine their life here.",
  photo_prep: "The morning of photos, every detail matters.",
  exterior: "First impressions start at the curb.",
  kitchen: "The #1 room buyers mentally redecorate. Keep it clean and uncluttered.",
  living_room: "Make it warm, open, and easy to picture their own furniture.",
  bedrooms: "Calm, light, and hotel-neutral beats personal and busy.",
  bathrooms: "Sparkling counters and fresh towels do serious work.",
  miscellaneous: "Everything that doesn't fit a room — closets, storage, laundry.",
};
const TITLES = Object.fromEntries(PREP_CATEGORIES.map((c) => [c.id, c.label]));
const ALL_KEYS = PREP_CATEGORIES.map((c) => c.id);

export default function PrepGuide() {
  const { p, refresh } = usePersonalization();
  const { content } = useContent();
  const branding = useBranding();
  const [state, setState] = useState({}); // { sectionKey: Set(ids) }
  const [newTitle, setNewTitle] = useState(Object.fromEntries(ALL_KEYS.map((k) => [k, ""])));
  const [askAgent, setAskAgent] = useState(Object.fromEntries(ALL_KEYS.map((k) => [k, false])));
  const [adding, setAdding] = useState(null); // section key currently submitting
  const [addErr, setAddErr] = useState({}); // sectionKey -> error message
  const [askSent, setAskSent] = useState({}); // sectionKey -> brief confirmation
  const askSentTimers = useRef({});
  useEffect(() => () => { Object.values(askSentTimers.current).forEach(clearTimeout); }, []);

  const BASE_SECTIONS = useMemo(() => (
    ALL_KEYS.map((k) => ({
      key: k,
      title: TITLES[k],
      blurb: content.prep_blurbs?.[k] ?? BLURBS[k],
      items: content.prep[k] || [],
      img: IMAGES[k],
    }))
  ), [content]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get("/progress");
      const initial = {};
      (data.progress || []).forEach((pp) => { initial[pp.section] = new Set(pp.completed_items || []); });
      setState(initial);
    })();
  }, []);

  // When seller lands on this page and has unseen agent replies, mark them seen
  // so the dashboard nudge clears.
  useEffect(() => {
    const unseen = (p.seller_tasks || []).some((t) => t.agent_reply && !t.agent_reply.seen);
    if (!unseen) return;
    (async () => {
      try {
        await api.post("/me/seller-task/replies-seen");
        refresh();
      } catch {}
    })();
  }, [p.seller_tasks, refresh]);

  // Merge custom tasks into default sections + add a dynamic "custom" section grouping.
  // Custom and seller-added items render at the TOP of each section (before defaults).
  // Items the seller has marked Not Applicable are siphoned off into a `hiddenItems`
  // list per section so they can be restored from a footer panel.
  const hiddenIds = useMemo(() => new Set(p.hidden_prep_items || []), [p.hidden_prep_items]);
  const sections = useMemo(() => {
    const byCat = Object.fromEntries(ALL_KEYS.map((k) => [k, []]));
    const customGroups = {}; // label -> items
    (p.custom_tasks || []).forEach((t) => {
      const item = {
        id: t.id,
        label: t.title,
        note: t.note,
        due_date: t.due_date,
        fromAgent: true,
      };
      if (t.category === "custom") {
        const label = t.custom_category_label || "From your agent";
        customGroups[label] = customGroups[label] || [];
        customGroups[label].push(item);
      } else if (byCat[t.category]) {
        byCat[t.category].push(item);
      }
    });
    // Seller's own tasks
    (p.seller_tasks || []).forEach((t) => {
      if (!byCat[t.category]) return;
      byCat[t.category].push({
        id: t.id,
        label: t.title,
        note: t.note,
        due_date: t.due_date,
        fromSeller: true,
        agentReply: t.agent_reply,
      });
    });

    const splitVisible = (arr) => arr.filter((x) => !hiddenIds.has(x.id));
    const splitHidden = (arr) => arr.filter((x) => hiddenIds.has(x.id));

    const base = BASE_SECTIONS.map((s) => {
      // Custom + seller items first, then the original defaults.
      const all = [...byCat[s.key].map((x) => ({ ...x })), ...s.items];
      return {
        ...s,
        items: splitVisible(all),
        hiddenItems: splitHidden(all),
      };
    });
    // Add custom groups as additional sections
    Object.entries(customGroups).forEach(([label, items], idx) => {
      const visible = splitVisible(items);
      const hidden = splitHidden(items);
      if (visible.length === 0 && hidden.length === 0) return;
      base.push({
        key: `custom_${idx}`,
        title: label,
        blurb: "Personalized by your agent for your home.",
        items: visible,
        hiddenItems: hidden,
        img: "https://images.unsplash.com/photo-1627518788331-b3b7fdaa382f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxyZWFsJTIwZXN0YXRlJTIwc2lnbmluZyUyMGRvY3VtZW50fGVufDB8fHx8MTc3Njc1MzU2OXww&ixlib=rb-4.1.0&q=85",
        custom: true,
      });
    });
    return base;
  }, [p, BASE_SECTIONS, hiddenIds]);

  const toggle = async (sectionKey, itemId, total) => {
    const cur = new Set(state[sectionKey] || []);
    cur.has(itemId) ? cur.delete(itemId) : cur.add(itemId);
    const next = { ...state, [sectionKey]: cur };
    setState(next);
    try {
      await api.post("/progress", {
        section: sectionKey,
        completed_items: Array.from(cur),
        total_items: total,
      });
    } catch {}
  };

  const addSellerTask = async (sectionKey) => {
    const title = (newTitle[sectionKey] || "").trim();
    if (!title) return;
    const ask = !!askAgent[sectionKey];
    setAdding(sectionKey);
    setAddErr((e) => ({ ...e, [sectionKey]: "" }));
    try {
      await api.post("/me/seller-task", { category: sectionKey, title, ask_agent: ask });
      setNewTitle((s) => ({ ...s, [sectionKey]: "" }));
      setAskAgent((a) => ({ ...a, [sectionKey]: false }));
      if (ask) {
        setAskSent((s) => ({ ...s, [sectionKey]: `Sent to ${branding.agent_first_name} — they'll reach out.` }));
        clearTimeout(askSentTimers.current[sectionKey]);
        askSentTimers.current[sectionKey] = setTimeout(
          () => setAskSent((s) => ({ ...s, [sectionKey]: "" })),
          4000
        );
      }
      await refresh();
    } catch (e) {
      setAddErr((x) => ({ ...x, [sectionKey]: e.response?.data?.detail || "Couldn't add — try again." }));
    }
    finally { setAdding(null); }
  };

  const removeSellerTask = async (taskId) => {
    // optimistic: drop from local checked state if present
    try {
      await api.delete(`/me/seller-task/${taskId}`);
      await refresh();
    } catch {}
  };

  const toggleHidden = async (itemId, hidden) => {
    try {
      await api.post("/me/prep-guide/hidden-item", { item_id: itemId, hidden });
      await refresh();
    } catch {}
  };

  return (
    <div className="container-x" style={{ padding: "48px 0 64px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12 }}>
        <div>
          <span className="overline">Prep guide</span>
          <h1 className="font-serif" style={{ fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 500, margin: "8px 0 12px", letterSpacing: "-0.01em" }}>
            Make your home unforgettable.
          </h1>
          <p style={{ color: "var(--text-muted)", maxWidth: 720, fontSize: "1.05rem" }}>
            Tap through each section. Your progress saves automatically and flows back to your agent.
          </p>
        </div>
        <PrintPdfButton />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 28, marginTop: 40 }}>
        {sections.map((s) => {
          const checked = state[s.key] || new Set();
          const pct = s.items.length ? Math.round((checked.size / s.items.length) * 100) : 0;
          return (
            <div key={s.key} className="card" style={{ padding: 0, overflow: "hidden" }} data-testid={`prep-section-${s.key}`}>
              <div className="prep-card-grid">
                <div className="prep-card-image">
                  <img src={s.img} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", position: "absolute", inset: 0 }} />
                </div>
                <div style={{ padding: 32 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <h2 className="font-serif" style={{ fontSize: 28, fontWeight: 500, margin: 0, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 8 }}>
                        {s.title}
                        {s.custom && <span className="badge" style={{ background: "#FBECE8", color: "var(--secondary)" }}>From your agent</span>}
                      </h2>
                      <p style={{ color: "var(--text-muted)", marginTop: 6 }}>{s.blurb}</p>
                    </div>
                    <span className="badge badge-success" data-testid={`prep-pct-${s.key}`}>{pct}% done</span>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    {s.items.map((item) => {
                      const isChecked = checked.has(item.id);
                      return (
                        <div
                          key={item.id}
                          data-testid={`prep-item-${item.id}`}
                          className={`check-item ${isChecked ? "checked" : ""}`}
                          onClick={() => toggle(s.key, item.id, s.items.length)}
                          role="button"
                        >
                          <div className="check-box">{isChecked && <Check size={14} color="#fff" strokeWidth={3} />}</div>
                          <div style={{ flex: 1 }}>
                            <div className="check-label" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <span>{item.label}</span>
                              {item.fromAgent && (
                                <span className="badge" style={{ background: "#FBECE8", color: "var(--secondary)", display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`from-agent-${item.id}`}>
                                  <Sparkles size={10} /> From your agent
                                </span>
                              )}
                              {item.fromSeller && (
                                <span className="badge" style={{ background: "var(--surface-alt)", color: "var(--primary)" }} data-testid={`from-seller-${item.id}`}>
                                  Added by you
                                </span>
                              )}
                            </div>
                            {item.note && <div style={{ fontSize: 13, color: "var(--text-muted)", marginTop: 2 }}>{item.note}</div>}
                            {item.due_date && <div style={{ fontSize: 12, color: "var(--secondary)", marginTop: 2 }}>Due {new Date(item.due_date).toLocaleDateString()}</div>}
                            {item.agentReply && (
                              <div
                                data-testid={`agent-reply-${item.id}`}
                                onClick={(e) => e.stopPropagation()}
                                style={{ marginTop: 10, padding: "10px 12px", borderLeft: "3px solid var(--primary)", background: "var(--surface-alt)", borderRadius: 8 }}
                              >
                                <div className="overline" style={{ color: "var(--primary)", display: "inline-flex", alignItems: "center", gap: 4 }}>
                                  <MessageCircle size={10} /> {branding.agent_first_name} says
                                </div>
                                <div style={{ fontSize: 14, color: "var(--text)", marginTop: 4, whiteSpace: "pre-wrap" }}>{item.agentReply.body}</div>
                                {item.agentReply.replied_at && (
                                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                                    {new Date(item.agentReply.replied_at).toLocaleString()}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {item.fromSeller ? (
                            <button
                              type="button"
                              aria-label="Remove task"
                              data-testid={`remove-seller-task-${item.id}`}
                              onClick={(e) => { e.stopPropagation(); removeSellerTask(item.id); }}
                              style={{ background: "transparent", border: "none", padding: 6, borderRadius: 8, color: "var(--text-muted)", cursor: "pointer", display: "inline-flex" }}
                              title="Remove this task"
                            >
                              <X size={14} />
                            </button>
                          ) : (
                            <button
                              type="button"
                              aria-label="Mark not applicable"
                              data-testid={`hide-item-${item.id}`}
                              onClick={(e) => { e.stopPropagation(); toggleHidden(item.id, true); }}
                              className="prep-na-btn"
                              title="Not applicable to my home — hide this item"
                            >
                              <EyeOff size={12} />
                              <span>N/A</span>
                            </button>
                          )}
                        </div>
                      );
                    })}

                    {!s.custom && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <input
                            data-testid={`add-task-input-${s.key}`}
                            className="input-field"
                            placeholder={`Add your own ${s.title.toLowerCase()} task…`}
                            value={newTitle[s.key] || ""}
                            onChange={(e) => setNewTitle((x) => ({ ...x, [s.key]: e.target.value }))}
                            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addSellerTask(s.key); } }}
                            style={{ flex: 1, minWidth: 220 }}
                            disabled={adding === s.key}
                          />
                          <button
                            type="button"
                            data-testid={`add-task-btn-${s.key}`}
                            className="btn-secondary"
                            onClick={() => addSellerTask(s.key)}
                            disabled={adding === s.key || !(newTitle[s.key] || "").trim()}
                            style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 16px", fontSize: 14 }}
                          >
                            <Plus size={14} /> Add task
                          </button>
                        </div>
                        <label
                          style={{ display: "inline-flex", alignItems: "center", gap: 8, marginTop: 10, fontSize: 13, color: "var(--text-muted)", cursor: "pointer" }}
                        >
                          <input
                            data-testid={`ask-agent-${s.key}`}
                            type="checkbox"
                            checked={!!askAgent[s.key]}
                            onChange={(e) => setAskAgent((a) => ({ ...a, [s.key]: e.target.checked }))}
                            style={{ accentColor: "var(--primary)" }}
                            disabled={adding === s.key}
                          />
                          <MessageCircle size={13} />
                          <span>Ask {branding.agent_first_name} about this — they'll get a note in their inbox.</span>
                        </label>
                        {askSent[s.key] && (
                          <div data-testid={`ask-agent-sent-${s.key}`} style={{ marginTop: 6, fontSize: 13, color: "var(--success)" }}>{askSent[s.key]}</div>
                        )}
                      </div>
                    )}
                    {addErr[s.key] && (
                      <div data-testid={`add-task-err-${s.key}`} style={{ marginTop: 8, color: "var(--secondary)", fontSize: 13 }}>{addErr[s.key]}</div>
                    )}
                    {s.hiddenItems && s.hiddenItems.length > 0 && (
                      <HiddenItemsPanel
                        sectionKey={s.key}
                        items={s.hiddenItems}
                        onRestore={(id) => toggleHidden(id, false)}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


function HiddenItemsPanel({ sectionKey, items, onRestore }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 18, paddingTop: 14, borderTop: "1px dashed var(--border)" }}>
      <button
        type="button"
        data-testid={`hidden-toggle-${sectionKey}`}
        onClick={() => setOpen((v) => !v)}
        style={{
          background: "transparent", border: "none", padding: 0,
          color: "var(--text-muted)", fontSize: 13, cursor: "pointer",
          display: "inline-flex", alignItems: "center", gap: 6,
        }}
      >
        <EyeOff size={12} />
        <span>{items.length} hidden {items.length === 1 ? "item" : "items"} {open ? "—  hide list" : "—  tap to review"}</span>
      </button>
      {open && (
        <div data-testid={`hidden-list-${sectionKey}`} style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
          {items.map((it) => (
            <div
              key={it.id}
              data-testid={`hidden-item-${it.id}`}
              style={{
                display: "flex", alignItems: "center", gap: 10,
                padding: "8px 12px", background: "var(--surface-alt)",
                borderRadius: 8, fontSize: 14,
              }}
            >
              <span style={{ flex: 1, color: "var(--text-muted)", textDecoration: "line-through" }}>{it.label}</span>
              <button
                type="button"
                data-testid={`restore-item-${it.id}`}
                onClick={() => onRestore(it.id)}
                className="btn-ghost"
                style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 12, padding: "4px 10px" }}
                title="Restore this item"
              >
                <RotateCcw size={12} /> Restore
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function PrintPdfButton() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const fetchPdfBlob = async () => {
    const res = await api.get("/me/prep-guide/pdf", { responseType: "blob" });
    const blob = new Blob([res.data], { type: "application/pdf" });
    const cd = res.headers?.["content-disposition"] || "";
    const m = cd.match(/filename="?([^"]+)"?/i);
    return { blob, filename: m ? m[1] : "prep_guide.pdf" };
  };

  const save = async () => {
    setBusy(true); setErr("");
    try {
      const { blob, filename } = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      setErr("Could not save — try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  const print = async () => {
    setBusy(true); setErr("");
    try {
      const { blob } = await fetchPdfBlob();
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank");
      if (!win) {
        // Pop-up blocked — fall back to a download.
        const a = document.createElement("a");
        a.href = url; a.download = "prep_guide.pdf";
        document.body.appendChild(a); a.click(); a.remove();
        setErr("Pop-up blocked — saved instead. Allow pop-ups to print directly.");
      } else {
        // Wait for the PDF viewer to render, then trigger print.
        win.addEventListener("load", () => {
          try { win.focus(); win.print(); } catch { /* user can print from the toolbar */ }
        });
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      setErr("Could not open the printable version — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <button
          type="button"
          data-testid="save-prep-guide-btn"
          onClick={save}
          disabled={busy}
          className="btn-secondary"
          style={{ padding: "10px 16px", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Save size={14} /> {busy ? "Working…" : "Save"}
        </button>
        <button
          type="button"
          data-testid="print-prep-guide-btn"
          onClick={print}
          disabled={busy}
          className="btn-secondary"
          style={{ padding: "10px 16px", fontSize: 14, display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          <Printer size={14} /> {busy ? "Working…" : "Print"}
        </button>
      </div>
      {err && <span data-testid="print-prep-err" style={{ color: "var(--secondary)", fontSize: 12 }}>{err}</span>}
    </div>
  );
}
